import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AddJobDialogComponent } from './add-job-dialog/add-job-dialog.component';
import { JobsService } from '../../services/jobs.service';
import { ITableCell } from '../../../../shared';
import {
    getPrettyTaskStatus2,
    getPrettyTaskType,
    getTaskDescriptionByTaskType,
    tasksStatuses
} from '../../../../shared/enums';
import { Observable, Subject, Subscription } from 'rxjs';
import { ITaskModel } from 'src/app/shared/interfaces/task-model.interface';
import * as _ from 'lodash';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { Sort } from '@angular/material/sort';
import ISearchProcessesPaginatedParams = ZoningAPI.ISearchProcessesPaginatedParams;
import { SEARCHABLE_JOBS_LIST } from '../../constants';
import { InstanceService } from '../../services/instance.service';
import { filter, takeUntil } from 'rxjs/operators';
import { Instance } from '../../models/instance';

const sortByDict = {
    typeStr: 'type',
    prettyStatus: 'status'
};

@Component({
    standalone: false,
    selector: 'app-jobs-tab',
    templateUrl: './jobs-tab.component.html',
    styleUrls: ['./jobs-tab.component.scss', '../../../../shared/styles/data-grid.scss']
})
export class JobsTabComponent implements OnInit, OnDestroy {
    @Input() jobs: Observable<ITaskModel[]>;
    @Input() hideAddButton: boolean;
    @Input() showSelectedColumn?: boolean;
    @ViewChild('selectAllCheckbox') selectAllCheckbox: MatCheckbox;
    @Input() state?: string;
    @Output() searchParams = new EventEmitter<ISearchProcessesPaginatedParams>();

    showSpinner = false;

    subscriptions: Subscription[] = [];

    constructor(
        private readonly dialog: MatDialog,
        private readonly jobsService: JobsService,
        private readonly instanceService: InstanceService
    ) {}

    attachmentsColumns: ITableCell[] = [
        { property: 'FileName', title: 'Name' },
        { property: 'attachmentTypeStr', title: 'Attachment Type' }
    ];

    attachmentsColumnsToDisplay: string[] = [...this.attachmentsColumns.map(zcvCell => zcvCell.property), 'Actions'];

    filter: ISearchProcessesPaginatedParams = {};
    sort: Sort;

    jobColumns: ITableCell[] = [
        { property: 'createdAt', title: 'Create Date', sortable: true, filter: 'createdAt', filterType: 'date' },
        {
            property: 'name',
            title: 'Name',
            sortable: true,
            filter: 'name',
            filterType: 'text',
            additionalClasses: 'cropped-label',
            showTitle: true
        },
        { property: 'typeStr', title: 'Type', sortable: true, filter: 'type', filterType: 'jobType' },
        { property: 'state', title: 'State', sortable: true, filter: 'state', filterType: 'state' },
        { property: 'progress', title: 'Progress, %', sortable: true },
        { property: 'totalPropertyRecords', title: 'Property Count', sortable: true },
        { property: 'emptyResultAttempts', title: 'Error count', sortable: true },
        { property: 'peakThreadsCountSafe', title: `Max Threads`, sortable: true },
        { property: 'activeThreads', getTitle: this.getThreadsTitle.bind(this), sortable: true },
        { property: 'prettyStatus', title: 'Status', sortable: true, filter: 'statuses', filterType: 'statuses' }
    ];

    jobColumnsToDisplay: string[] = [];

    expandedElement: ITaskModel;
    totalActiveThreadsCount: number;

    currentJobs: ITaskModel[];

    tableId = _.uniqueId('job-tab-');

    getTaskDescriptionByTaskType = getTaskDescriptionByTaskType;
    getPrettyTaskType = getPrettyTaskType;
    jobsList = SEARCHABLE_JOBS_LIST;

    tasksStatuses = tasksStatuses;
    getPrettyTaskStatus2 = getPrettyTaskStatus2;

    destroy$ = new Subject<boolean>();

    instances: Instance[];

    ngOnInit() {
        this.jobColumnsToDisplay = this.showSelectedColumn
            ? ['selected', ...this.jobColumns.map(zcvCell => zcvCell.property), 'actions']
            : [...this.jobColumns.map(zcvCell => zcvCell.property), 'actions'];
        this.subscriptions.push(
            this.jobs.subscribe(jobs => {
                this.currentJobs = jobs;
                this.showSpinner = false;
                this.totalActiveThreadsCount = jobs.reduce((acc, process) => {
                    return acc + _.get(process, 'activeThreads', 0);
                }, 0);
                if (this.selectAllCheckbox) {
                    this.selectAllCheckbox.checked = false;
                }
            })
        );
        this.instanceService.instances
            .pipe(
                takeUntil(this.destroy$),
                filter(instances => instances.length > 0)
            )
            .subscribe(instances => {
                this.instances = instances;
            });
    }

    newTaskDialog() {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '50%';
        dialogConfig.panelClass = 'job-dialog';
        const dialogRef = this.dialog.open(AddJobDialogComponent, dialogConfig);
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.showSpinner = true;
            }
        });
    }

    showAttachnments(job: ITaskModel) {
        if (this.expandedElement && this.expandedElement.id === job.id) {
            this.expandedElement = null;
            return;
        }

        this.expandedElement = job;

        if (!this.expandedElement.hasAttachments) {
            this.jobsService.getJobAttachments(this.expandedElement);
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => !subscription.closed && subscription.unsubscribe());
    }

    getThreadsTitle() {
        return `Current Threads (${this.totalActiveThreadsCount})`;
    }

    setSelectedAllJobs(event: MatCheckboxChange) {
        if (_.isEmpty(this.currentJobs)) {
            return;
        }

        this.currentJobs.forEach(job => {
            job.isSelected = event.checked;
        });
    }

    sortData(sort: Sort) {
        this.sort = sort;
        this.emitSearchParams();
    }

    filterClick(event: PointerEvent) {
        if (!event) {
            return;
        }

        event.stopPropagation();
    }

    filterChange() {
        this.emitSearchParams();
    }

    getProcessSearchParams() {
        const params: ISearchProcessesPaginatedParams = _.cloneDeep(this.filter);
        if (_.get(this.sort, 'active') && _.get(this.sort, 'direction')) {
            params.sortBy = sortByDict[this.sort.active] || this.sort.active;
            params.sortOrder = this.sort.direction;
        } else {
            delete params['sortBy'];
            delete params['sortOrder'];
        }
        return params;
    }

    emitSearchParams() {
        const searchParams = this.getProcessSearchParams();
        this.searchParams.emit(searchParams);
    }

    getHeaderClass(columnConfig: ITableCell) {
        return { ['jobs-column-' + columnConfig.property]: true, 'filter-header-cell': !!columnConfig.filterType };
    }

    getCellClass(columnConfig: ITableCell) {
        return `${columnConfig.additionalClasses ? columnConfig.additionalClasses : ''}`;
    }
}
